// Features.js
import React from 'react'
import { motion } from 'framer-motion'
import { StaticImage } from 'gatsby-plugin-image'
import { 
  HiTemplate, 
  HiClock, 
  HiChartBar, 
  HiUserGroup, 
  HiCube, 
  HiDeviceMobile, 
  HiSparkles
} from 'react-icons/hi'

const Features = () => {
  const features = [
    {
      icon: <HiTemplate className="w-6 h-6" />,
      title: "AI-Powered Resume Templates",
      description: "Choose from multiple templates optimized by hiring experts and AI algorithms. Each template is ATS-tested and recruiter-approved.",
    },
    {
      icon: <HiChartBar className="w-6 h-6" />,
      title: "Job Match Analytics",
      description: "Get real-time scoring and insights on how well your resume matches specific job descriptions. Receive targeted improvement suggestions.",
    },
    {
      icon: <HiSparkles className="w-6 h-6" />,
      title: "One-Click Generation",
      description: "Instantly generate tailored resumes, cover letters, and follow-up emails that match job descriptions with a single click.",
    },
    {
      icon: <HiClock className="w-6 h-6" />,
      title: "5-Minute Resume Builder",
      description: "Transform your LinkedIn profile or existing resume into multiple targeted versions using our AI-powered quick builder.",
    },
    {
      icon: <HiCube className="w-6 h-6" />,
      title: "Smart ATS Optimization",
      description: "Achieve 95% ATS pass rate with our keyword optimization engine. Automatically format your resume to pass any applicant tracking system.",
    },
    {
      icon: <HiDeviceMobile className="w-6 h-6" />,
      title: "Cross-Platform Access",
      description: "Create, edit, and download your professional documents from any device. Cloud sync ensures your latest version is always available.",
    }
  ]

  return (
    <div className="relative py-24 bg-white overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-indigo-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-purple-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 bg-indigo-50 rounded-full px-4 py-2 mb-6">
            <HiSparkles className="w-5 h-5 text-indigo-600" />
            <span className="text-indigo-600 text-sm font-medium">
            Powerful Features
            </span>
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
            Discover your path to the <span className="text-indigo-600">perfect resume</span>
          </h2>
          <p className="text-md text-neutral-700 max-w-2xl mx-auto">
            ResumeFlex empowers you with a flexible, engaging platform to create professional resumes, 
            connect with experts, and track your application success.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="h-full p-8 bg-white rounded-2xl border border-neutral-100 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity" />
                <div className="relative">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Demo Section */}
        <motion.div 
          className="mt-20 p-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold text-neutral-900">
                Start crafting your professional resume today
              </h3>
              <p className="text-md text-neutral-700">
                Join thousands of job seekers who have successfully landed their dream jobs using ResumeFlex.
              </p>
              <div className="flex gap-4">
                <motion.button 
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-md"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open("https://app.resumeflex.com", "_blank")}
                >
                  Try Now                  
                </motion.button>
                <motion.button 
                  className="bg-white text-neutral-700 px-6 py-3 rounded-lg text-sm font-medium border border-neutral-200 hover:bg-neutral-50 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Watch Demo
                </motion.button>
              </div>
            </div>
            <div className="flex-1">
              <StaticImage
                src="../images/resume-analytics.png"
                alt="ResumeFlex Analytics Dashboard"
                className="rounded-lg shadow-lg"
                placeholder="blurred"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Features
